import { useCallback, useEffect, useMemo, useState } from "react";
import type { AttentionItem } from "@/components/AttentionItemDetailModal";
import { ACTION_STATUSES, ActionQueueService, type ActionQueueRow } from "@/services/action-queue.service";

function toSeverity(urgency: string | null | undefined): "critical" | "warning" {
  return urgency === "high" ? "critical" : "warning";
}

function toPrimaryLink(row: ActionQueueRow): string {
  const payload = row.payload ?? {};
  if (payload.job_id) return `/jobs/${payload.job_id}`;
  if (row.entity_type === "permit" || row.action_key === "resolve_permit_issue") return "/permitting";
  if (row.entity_type === "qa" || row.action_key === "resolve_qa_issue") return "/qa";
  return "/jobs";
}

function toSecondaryLink(row: ActionQueueRow): string {
  const payload = row.payload ?? {};
  if (row.action_key === "resolve_permit_issue") return "/permitting";
  if (row.action_key === "resolve_qa_issue") return "/qa";
  return payload.job_id ? `/jobs/${payload.job_id}` : "/jobs";
}

function mapRowToAttentionItem(row: ActionQueueRow): AttentionItem {
  const payload = row.payload ?? {};
  const headline = (payload.headline || row.primary_action || row.action_key || "Action Required").trim();
  const context = (payload.context || "").trim();
  const whyItMatters = (payload.why_it_matters || "").trim();

  const details: string[] = [];
  if (context) details.push(context);
  if (row.primary_action) details.push(`Primary Action: ${row.primary_action}`);
  if (row.secondary_action) details.push(`Secondary Action: ${row.secondary_action}`);
  if (payload.suggested_action) details.push(`Suggested Action: ${payload.suggested_action}`);
  if (payload.blocker_category) details.push(`Blocker: ${payload.blocker_category}`);

  return {
    id: row.id,
    title: headline,
    entity: row.entity_type === "issue" ? "Issue" : "Job",
    reason: [context, row.blocked ? "⛔ BLOCKED" : "", row.primary_action ? `Action: ${row.primary_action}` : ""].filter(Boolean).join(" · "),
    severity: toSeverity(row.urgency),
    link: toPrimaryLink(row),
    age: row.blocked ? "Blocked" : undefined,
    details,
    whyItMatters: whyItMatters || undefined,
    primaryAction: row.primary_action ?? undefined,
    secondaryAction: row.secondary_action ?? "Open Record",
    secondaryLink: toSecondaryLink(row),
    jobId: payload.job_id,
    issueId: payload.issue_id,
  };
}

export function useActionQueue(): {
  items: AttentionItem[];
  hasAIData: boolean;
  totalRevenueAtRisk: string;
  refresh: () => Promise<void>;
} {
  const [rows, setRows] = useState<ActionQueueRow[]>([]);

  const refresh = useCallback(async () => {
    const { data, error } = await ActionQueueService.getPending();
    if (error) {
      console.warn("[useActionQueue] Failed to load pending queue items:", error);
      setRows([]);
      return;
    }
    setRows(data ?? []);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const result = useMemo(() => {
    const pending = rows.filter((row) => row.status === ACTION_STATUSES.PENDING || row.status === ACTION_STATUSES.IN_PROGRESS);
    const items = pending.map(mapRowToAttentionItem);
    const total = pending.reduce((sum, row) => sum + (row.payload?.amount_at_risk ?? 0), 0);

    return {
      items,
      hasAIData: items.length > 0,
      totalRevenueAtRisk: `$${total.toLocaleString()}`,
    };
  }, [rows]);

  return {
    ...result,
    refresh,
  };
}
